import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatRadioButton } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  isObservable,
  map,
  Observable,
  of,
  startWith,
} from 'rxjs';

export interface MultipleRequestedServicesFormFieldValue {
  draftValues: any[];
  values: any[];
  isDraft?: boolean;
  customPrices?: { offeredServiceId: number; price: number }[];
}
export interface SingleRequestedServicesFormFieldValue {
  draftValue: any;
  value: any;
  isDraft?: boolean;
  customPrices?: { offeredServiceId: number; price: number }[];
}

export type RequestedServicesFormFieldValue =
  | SingleRequestedServicesFormFieldValue
  | MultipleRequestedServicesFormFieldValue;

@Component({
  selector: 'aa-requested-services-form-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatInputModule,
    MatRadioButton,
  ],
  templateUrl: './requested-services-form-field.component.html',
  styleUrl: './requested-services-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestedServicesFormFieldComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private cdr = inject(ChangeDetectorRef);
  fieldOptions$!: Observable<{ label: string; value: any; meta?: any }[]>;
  isDraft$ = new BehaviorSubject(true);

  value$ = new BehaviorSubject<RequestedServicesFormFieldValue | null>(null);
  customPriceMap: Record<number, number | null> = {};

  ngOnInit() {
    if (isObservable(this.props.options)) {
      this.fieldOptions$ = this.props.options;
    } else {
      this.fieldOptions$ = of(this.props.options as any[]);
    }

    this.formControl.valueChanges
      .pipe(startWith(this.formControl.value))
      .subscribe(async (value) => {
        this.syncCustomPricesFromValue(value);
        this.value$.next(value);
      });

    this.isDraft$.subscribe(async (isDraft) => {
      const currentValue = await firstValueFrom(this.value$);
      let additionalAdjustments: Partial<
        | MultipleRequestedServicesFormFieldValue
        | SingleRequestedServicesFormFieldValue
      > = {};

      if (
        isDraft &&
        (this.props as any).multiple &&
        (!(currentValue as MultipleRequestedServicesFormFieldValue)
          ?.draftValues ||
          (currentValue as MultipleRequestedServicesFormFieldValue)?.draftValues
            .length == 0) &&
        !this.props['hasPossibleEmptyPendingChanges']
      ) {
        additionalAdjustments = {
          ...additionalAdjustments,
          draftValues: [
            ...(currentValue as MultipleRequestedServicesFormFieldValue).values,
          ],
        };
      } else if (
        isDraft &&
        !(this.props as any).multiple &&
        !(currentValue as SingleRequestedServicesFormFieldValue)?.draftValue
      ) {
        additionalAdjustments = {
          ...additionalAdjustments,
          draftValue: (currentValue as SingleRequestedServicesFormFieldValue)
            ?.value,
        };
      }

      this.formControl.setValue({
        ...currentValue,
        isDraft,
        ...additionalAdjustments,
      });
    });

    if ((this.props as any).externalDraftToggleObservable) {
      (
        (this.props as any).externalDraftToggleObservable as Observable<boolean>
      ).subscribe((isDraft) => {
        this.isDraft$.next(isDraft);
      });
    }
  }

  async onOptionClicked(value: any) {
    this.formControl.markAsDirty();
    const isDraft = await firstValueFrom(this.isDraft$);
    const currentValue = await firstValueFrom(this.value$);
    let newValue;
    if ((this.props as any).multiple) {
      if (currentValue) {
        if (isDraft) {
          const draftContains = (
            currentValue as MultipleRequestedServicesFormFieldValue
          ).draftValues.includes(value);

          newValue = {
            ...currentValue,
            draftValues: draftContains
              ? (
                  currentValue as MultipleRequestedServicesFormFieldValue
                ).draftValues.filter((item) => item != value)
              : [
                  value,
                  ...(currentValue as MultipleRequestedServicesFormFieldValue)
                    .draftValues,
                ],
          };
        } else {
          const valuesContains = (
            currentValue as MultipleRequestedServicesFormFieldValue
          ).values.includes(value);

          newValue = {
            ...currentValue,
            values: valuesContains
              ? (
                  currentValue as MultipleRequestedServicesFormFieldValue
                ).values.filter((item) => item != value)
              : [
                  value,
                  ...(currentValue as MultipleRequestedServicesFormFieldValue)
                    .values,
                ],
          };
        }
      } else {
        newValue = {
          isDraft,
          draftValues: isDraft ? [value] : [],
          values: isDraft ? [] : [value],
        };
      }
    } else {
      if (currentValue) {
        newValue = {
          ...currentValue,
          draftValue: isDraft
            ? value
            : (currentValue as SingleRequestedServicesFormFieldValue)
                .draftValue,
          value: isDraft
            ? (currentValue as SingleRequestedServicesFormFieldValue).value
            : value,
        };
      } else {
        newValue = {
          draftValue: isDraft ? value : undefined,
          value: isDraft ? undefined : value,
          isDraft,
        };
      }
    }

    this.formControl.setValue(newValue);
  }

  async toggleIsDraft() {
    this.isDraft$.next(!(await firstValueFrom(this.isDraft$)));
  }

  optionNeedsCustomPrice(option: { value: any; meta?: any }) {
    return combineLatest([this.isDraft$, this.value$]).pipe(
      map(([isDraft, currentValue]) => {
        if (!option.meta || option.meta.price != null) return false;
        if (!isDraft) return false;
        if ((this.props as any).multiple) {
          const mv = currentValue as MultipleRequestedServicesFormFieldValue;
          const inDraft = !!mv?.draftValues?.includes(option.value);
          const inCommitted = !!mv?.values?.includes(option.value);
          return inDraft && !inCommitted;
        } else {
          const sv = currentValue as SingleRequestedServicesFormFieldValue;
          return sv?.draftValue == option.value && sv?.value != option.value;
        }
      }),
    );
  }

  getCustomPrice(offeredServiceId: number) {
    return this.value$.pipe(
      map((value) => {
        if (this.customPriceMap[offeredServiceId] != null) {
          return this.customPriceMap[offeredServiceId];
        }
        return value?.customPrices?.find(
          (cp) => cp.offeredServiceId == offeredServiceId,
        )?.price ?? null;
      }),
    );
  }

  private syncCustomPricesFromValue(value: RequestedServicesFormFieldValue | null) {
    if (value?.customPrices?.length && Object.keys(this.customPriceMap).length === 0) {
      for (const cp of value.customPrices) {
        this.customPriceMap[cp.offeredServiceId] = cp.price;
      }
      this.cdr.markForCheck();
    }
  }

  onCustomPriceChanged(offeredServiceId: number, value: string) {
    const parsed = parseFloat(value);
    this.customPriceMap[offeredServiceId] = isNaN(parsed) ? null : parsed;
    this.formControl.markAsDirty();
    const currentValue = this.value$.getValue();
    if (currentValue) {
      const customPrices = Object.entries(this.customPriceMap)
        .filter(([, price]) => price != null)
        .map(([id, price]) => ({ offeredServiceId: +id, price: price! }));
      this.formControl.setValue({ ...currentValue, customPrices });
    }
  }

  getOptionValueChecked(value: any) {
    return combineLatest([this.isDraft$, this.value$]).pipe(
      map(([isDraft, currentValue]) => {
        if (isDraft) {
          if ((this.props as any).multiple) {
            return !!(
              currentValue as MultipleRequestedServicesFormFieldValue
            )?.draftValues?.find((i) => i == value);
          } else {
            return (
              (currentValue as SingleRequestedServicesFormFieldValue)
                ?.draftValue == value
            );
          }
        } else {
          if ((this.props as any).multiple) {
            return !!(
              currentValue as MultipleRequestedServicesFormFieldValue
            )?.values?.find((i) => i == value);
          } else {
            return (
              (currentValue as SingleRequestedServicesFormFieldValue)?.value ==
              value
            );
          }
        }
      }),
    );
  }
}
